import { ApolloError, useQuery, FetchPolicy } from '@apollo/client';
import { useContext, useMemo } from 'react';

import { useAuth } from '@providers/AuthProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import INSURANCE from '@graphql/queries/insurances.query.gql';
import MEMBER_INSURANCE_INFO from '@graphql/queries/memberInsuranceInfos.query.gql';
import { doesNotExist } from '@utils/general';
import { filter, find, map, omit } from 'ramda';

export type IInsurance = {
	id: string;
	inNetwork: boolean;
	insuranceProviderName: string;
	isDefault: boolean;
	isDeleted: boolean;
	name: string;
	policyHolderFirstName: string;
	policyHolderLastName: string;
	policyHolderDob: string;
	relationship: string;
	memberInsuranceId: string;
	rxPcn: string;
	rxGroup: string;
	rxBin: string;
	valid: boolean;
};

export type IInsuranceInfo = {
	id: string;
	insuranceProviderUuid: string;
	insuredMemberFirstName: string;
	insuredMemberLastName: string;
};

export type IMemberInsurance = (IInsurance & IInsuranceInfo) | IInsurance;

type UseMemberInsurance = (
	showDeleted?: boolean,
	fetchPolicy?: FetchPolicy
) => {
	loading: boolean;
	memberInsurance?: Array<IMemberInsurance>;
	error?: ApolloError;
	refetchMemberInsurance?: () => void;
};

interface IGqlInsurance {
	id: string;
	payer: {
		name: string;
		inNetwork: boolean;
	};
	isDefault: boolean;
	isDeleted: boolean;
	name: string;
	policyHolderFirstName: string;
	policyHolderLastName: string;
	policyHolderDob: string;
	relationship: string;
	memberInsuranceId: string;
	rxPcn: string;
	rxGroup: string;
	rxBin: string;
	valid: boolean;
}

interface IGqlInsurances {
	authenticatedMember: {
		insurances: IGqlInsurance[];
	};
}

interface IGqlInsuranceInfo {
	id: string;
	insuranceProviderUuid: string;
	insuredMemberFirstName: string;
	insuredMemberLastName: string;
}

interface IGqlInsuranceInfos {
	memberInsuranceInfos: {
		nodes: IGqlInsuranceInfo[];
	};
}

const useMemberInsurance: UseMemberInsurance = (
	showDeleted = false,
	fetchPolicy = 'cache-first'
) => {
	const { loggedIn } = useAuth();

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const {
		loading: insuranceLoading,
		error: insuranceError,
		data: insuranceData,
		refetch: refetchInsurance,
	} = useQuery<IGqlInsurances>(INSURANCE, {
		fetchPolicy,
		client: graphQLv2Client,
		skip: !loggedIn,
	});

	const {
		loading: insuranceInfoLoading,
		data: insuranceInfoData,
		refetch: refetchInsuranceInfo,
	} = useQuery<IGqlInsuranceInfos>(MEMBER_INSURANCE_INFO, {
		fetchPolicy,
		skip: !loggedIn,
	});

	const refetchMemberInsurance = () => {
		refetchInsurance();
		refetchInsuranceInfo();
	};

	const loading = insuranceLoading || insuranceInfoLoading;

	const memberInsurance = useMemo(() => {
		if (loading || doesNotExist(insuranceData?.authenticatedMember?.insurances)) {
			return undefined;
		}

		if (doesNotExist(insuranceInfoData?.memberInsuranceInfos?.nodes)) {
			return insuranceData?.authenticatedMember?.insurances?.map((insurance) => {
				return {
					...omit(['payer'], insurance),
					insuranceProviderName: insurance.payer.name,
				};
			});
		}

		const insuranceInfos = insuranceInfoData?.memberInsuranceInfos?.nodes ?? [];
		const insurances: IMemberInsurance[] | undefined = filter((insurance: IMemberInsurance) => {
			return showDeleted || !insurance.isDeleted;
		})(
			map((insurance) => {
				const insuranceInfo = find((info) => {
					return info.id === insurance.id;
				}, insuranceInfos);
				return {
					...(insuranceInfo ?? {}),
					...omit(['payer'], insurance),
					insuranceProviderName: insurance.payer?.name,
				};
			}, insuranceData?.authenticatedMember?.insurances ?? [])
		);

		return insurances;
	}, [loading, insuranceData, insuranceInfoData]);

	return {
		loading,
		memberInsurance,
		error: insuranceError,
		refetchMemberInsurance,
	};
};

export default useMemberInsurance;
